//employeetimemangement
import { useState, useEffect, useCallback, useRef } from "react";

import { db } from "../firebase/config";
import {
  doc,
  setDoc,
  getDoc,
  getDocs,
  collection,
  updateDoc,
  query,
  onSnapshot,
  where,
  orderBy,
  addDoc, // ✅ ADD
} from "firebase/firestore";
import "../styles/TimeManagement.css";
import { Link } from "react-router-dom";
import { DateTime } from "luxon";
import { serverTimestamp } from "firebase/firestore";
import { useAuth } from "../Context/AuthContext";

const DEBUG = true; // set to false in production
const GEO_TIMEOUT_MS = 8000; // 8s timeout for GPS

// Small helper: ISO in TZ (keep your existing one or leave as is)
const isoInTZ = (d, tz) => DateTime.fromISO(d, { zone: tz });

// "HH:mm" -> "h:mm a" in company TZ (ex: "17:00" -> "5:00 PM")
const formatShiftTime = (timeHHmm, tz) => {
  if (!timeHHmm) return "—";
  const dt = DateTime.fromFormat(timeHHmm, "HH:mm", { zone: tz });
  return dt.isValid ? dt.toFormat("h:mm a") : timeHHmm; // fallback
};

/** Try to get precise browser GPS (does NOT block if denied). */
const getBrowserLocation = () =>
  new Promise((resolve) => {
    if (typeof window === "undefined" || !("geolocation" in navigator)) {
      return resolve(null);
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy ?? null,
        });
      },
      () => {
        // user denied or error → just resolve null
        resolve(null);
      },
      {
        enableHighAccuracy: true,
        timeout: GEO_TIMEOUT_MS,
        maximumAge: 0,
      },
    );
  });

/** Get public IP via ipify (works in dev + prod). */
const getPublicIP = async () => {
  try {
    const res = await fetch("https://api.ipify.org?format=json");
    const data = await res.json();
    return data.ip;
  } catch (e) {
    if (DEBUG) console.warn("getPublicIP failed:", e);
    return "unknown";
  }
};

/** Convert IP → approximate city/region/country via ipapi.co */
const getLocationFromIP = async (ip) => {
  if (!ip || ip === "unknown") return null;

  try {
    const res = await fetch(`https://ipapi.co/${ip}/json/`);
    const data = await res.json();

    if (data.error) return null;

    return {
      city: data.city || null,
      region: data.region || null,
      country: data.country_name || null,
      lat: data.latitude || null,
      lng: data.longitude || null,
    };
  } catch (e) {
    if (DEBUG) console.warn("IP geolocation failed:", e);
    return null;
  }
};

/**
 * Hybrid location:
 * - Try GPS
 * - Always get IP
 * - Try IP → city
 * Returns an object ready to save as `clockInLocation`.
 */
const getLocation = async () => {
  const [ip, gps] = await Promise.all([getPublicIP(), getBrowserLocation()]);

  const ipGeo = await getLocationFromIP(ip);

  // Case 1: GPS allowed ➜ high precision
  if (gps) {
    if (DEBUG) console.log("[Location] Using GPS + IP data");
    return {
      precision: "gps",
      ip: ip || null,
      lat: gps.lat,
      lng: gps.lng,
      accuracy: gps.accuracy,
      // include city/region if IP API gave it
      city: ipGeo?.city || null,
      region: ipGeo?.region || null,
      country: ipGeo?.country || null,
    };
  }

  // Case 2: No GPS but we have IP geolocation
  if (ipGeo) {
    if (DEBUG) console.log("[Location] Using IP-only geolocation");
    return {
      precision: "ip-only",
      ip: ip || null,
      city: ipGeo.city,
      region: ipGeo.region,
      country: ipGeo.country,
      lat: ipGeo.lat,
      lng: ipGeo.lng,
    };
  }

  // Case 3: Only IP, no geodata
  if (ip && ip !== "unknown") {
    if (DEBUG) console.log("[Location] Using raw IP only");
    return {
      precision: "ip-only",
      ip,
    };
  }

  // Case 4: Nothing worked
  if (DEBUG) console.log("[Location] No location available");
  return {
    precision: "none",
  };
};

function materializeRepeatingWeek(baseSchedule, targetWeekStartISO, tz) {
  const baseStart = isoInTZ(baseSchedule.weekStartDate, tz);
  const targetStart = isoInTZ(targetWeekStartISO, tz);
  const deltaDays = Math.round(targetStart.diff(baseStart, "days").days);

  const shifts = (baseSchedule.shifts || []).map((s) => {
    const newDate = isoInTZ(s.date, tz)
      .plus({ days: deltaDays })
      .toISODate();
    return { ...s, date: newDate };
  });

  return {
    ...baseSchedule,
    weekStartDate: targetWeekStartISO,
    weekEndDate: targetStart.plus({ days: 6 }).toISODate(),
    shifts,
    _fromRecurring: baseSchedule.weekStartDate,
  };
}

function scheduleCoveringDate(allSchedules, dateISO, tz) {
  const authored = allSchedules.find(
    (s) => s.weekStartDate <= dateISO && dateISO <= s.weekEndDate,
  );
  if (authored) return authored;

  const candidates = allSchedules
    .filter((s) => s.repeatWeekly && s.weekStartDate <= dateISO)
    .sort((a, b) => b.weekStartDate.localeCompare(a.weekStartDate));

  if (candidates.length) {
    const targetWeekStartISO = DateTime.fromISO(dateISO, { zone: tz })
      .startOf("week")
      .toISODate();
    return materializeRepeatingWeek(candidates[0], targetWeekStartISO, tz);
  }
  return null;
}

const sendCompanyNotification = async (companyId, docId, payload) => {
  const nref = doc(db, `companies/${companyId}/notifications`, docId);
  await setDoc(
    nref,
    { archived: false, read: false, timestamp: serverTimestamp(), ...payload },
    { merge: true },
  );
};

const sendUserNotification = async (userId, payload, docId = null) => {
  if (docId) {
    const ref = doc(db, `users/${userId}/notifications`, docId);
    await setDoc(
      ref,
      {
        read: false,
        archived: false,
        createdAt: serverTimestamp(),
        ...payload,
      },
      { merge: true },
    );
    return;
  }

  const nref = collection(db, `users/${userId}/notifications`);
  await addDoc(nref, {
    read: false,
    archived: false,
    createdAt: serverTimestamp(),
    ...payload,
  });
};

/** PURE: compute status with second-precision comparisons (same TZ) */
function determineStatus(now, shiftStart, settings) {
  now = now.set({ second: 0, millisecond: 0 });
  shiftStart = shiftStart.set({ second: 0, millisecond: 0 });
  const {
    enableEarlyClockIn = true,
    earlyClockInMinutes = 15,
    enableLateGrace = true,
    lateGraceMinutes = 5,
  } = settings ?? {};

  if (now < shiftStart) {
    const earlySecs = Math.max(
      0,
      Math.floor(shiftStart.diff(now, "seconds").seconds),
    );
    if (!enableEarlyClockIn) return { status: "BLOCK_EARLY", earlySecs };
    const allowedEarlySecs = (earlyClockInMinutes ?? 15) * 60;
    if (earlySecs > allowedEarlySecs)
      return { status: "BLOCK_EARLY", earlySecs };
    return { status: "Early Clock-in", earlySecs };
  }

  if (now > shiftStart) {
    const lateSecs = Math.max(
      0,
      Math.floor(now.diff(shiftStart, "seconds").seconds),
    );
    const graceSecs = (lateGraceMinutes ?? 5) * 60;
    if (enableLateGrace && lateSecs <= graceSecs)
      return { status: "On Time", lateSecs };
    return { status: "Late Clock-in", lateSecs };
  }

  return { status: "On Time", lateSecs: 0, earlySecs: 0 };
}

/** Get week start (Mon) in company TZ for a given DateTime */
function getWeekStartISO(dt) {
  return dt.startOf("week").toISODate(); // Luxon weeks start Monday
}

export default function TimeManagement() {
  const [entries, setEntries] = useState([]); // today's entries (list)
  const [openEntryDoc, setOpenEntryDoc] = useState(null); // latest open entry (any day)
  const [dailyHours, setDailyHours] = useState(0);
  const [weeklyHours, setWeeklyHours] = useState(0);
  const [assignedShift, setAssignedShift] = useState(null);
  const [error, setError] = useState("");
  const [companyTimeZone, setCompanyTimeZone] = useState("UTC");
  const [tzLoaded, setTzLoaded] = useState(false);
  const [ttSettings, setTtSettings] = useState({
    enableEarlyClockIn: true,
    earlyClockInMinutes: 15,
    enableLateGrace: true,
    lateGraceMinutes: 5,
    overtimeBasis: "weekly", // "weekly" / "daily"
    hoursBeforeOvertimePerPeriod: 40,
    dailyOvertimeHours: 8,
  });
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const employeeCompanyIdRef = useRef(null);

  // live clock in company TZ
  const [nowTz, setNowTz] = useState(DateTime.now().setZone(companyTimeZone));

  // Weekly / Range timeline modal state
  const [weekModalOpen, setWeekModalOpen] = useState(false);
  const [weekLoading, setWeekLoading] = useState(false);
  const [weekStartISO, setWeekStartISO] = useState(null); // base for prev/next week
  const [weekRows, setWeekRows] = useState([]);
  const [weekTotalHours, setWeekTotalHours] = useState(0);

  // Explicit range that the timeline currently covers
  const [rangeStartISO, setRangeStartISO] = useState("");
  const [rangeEndISO, setRangeEndISO] = useState("");

  // Overtime summary for current range
  const [rangeOvertime, setRangeOvertime] = useState({
    total: 0,
    regular: 0,
    overtime: 0,
  });
  const { user } = useAuth();

  const resolveEmployeeCompany = useCallback(async () => {
    if (!user?.uid) throw new Error("User not authenticated.");

    // ✅ Use cached companyId if we already resolved it
    if (employeeCompanyIdRef.current) return employeeCompanyIdRef.current;

    // ✅ 1) Prefer AuthContext
    let cid = user.companyId || null;

    // ✅ 2) Fallback: users/{uid}.companyId
    if (!cid) {
      const userSnap = await getDoc(doc(db, "users", user.uid));
      cid = userSnap.data()?.companyId || null;
    }

    if (!cid) throw new Error("No company linked to this account.");

    employeeCompanyIdRef.current = cid;
    return cid;
  }, [user?.uid, user?.companyId]);

  const fetchCompanyTimeZone = useCallback(async (companyId) => {
    const companyDoc = await getDoc(doc(db, "companies", companyId));
    const tz = companyDoc.data()?.timeZone || "UTC";
    setCompanyTimeZone(tz);
    setTzLoaded(true);
    return tz;
  }, []);

  const fetchTimeTrackingSettings = useCallback(async (cid) => {
    try {
      const sref = doc(db, `companies/${cid}/settings/timeTracking`);
      const snap = await getDoc(sref);
      if (snap.exists()) {
        const d = snap.data() || {};
        const basis =
          d.overtimeBasis === "daily" || d.overtimeBasis === "weekly"
            ? d.overtimeBasis
            : "weekly";
        const perPeriod =
          typeof d.hoursBeforeOvertimePerPeriod === "number"
            ? d.hoursBeforeOvertimePerPeriod
            : 40;
        const daily =
          typeof d.dailyOvertimeHours === "number" ? d.dailyOvertimeHours : 8;

        setTtSettings({
          enableEarlyClockIn:
            typeof d.enableEarlyClockIn === "boolean"
              ? d.enableEarlyClockIn
              : true,
          earlyClockInMinutes:
            typeof d.earlyClockInMinutes === "number"
              ? d.earlyClockInMinutes
              : 15,
          enableLateGrace:
            typeof d.enableLateGrace === "boolean" ? d.enableLateGrace : true,
          lateGraceMinutes:
            typeof d.lateGraceMinutes === "number" ? d.lateGraceMinutes : 5,
          overtimeBasis: basis,
          hoursBeforeOvertimePerPeriod: perPeriod,
          dailyOvertimeHours: daily,
        });
      } else {
        setTtSettings({
          enableEarlyClockIn: true,
          earlyClockInMinutes: 15,
          enableLateGrace: true,
          lateGraceMinutes: 5,
          overtimeBasis: "weekly",
          hoursBeforeOvertimePerPeriod: 40,
          dailyOvertimeHours: 8,
        });
      }
      setSettingsLoaded(true);
    } catch (e) {
      console.warn("Failed to load time tracking settings:", e?.message);
      setTtSettings({
        enableEarlyClockIn: true,
        earlyClockInMinutes: 15,
        enableLateGrace: true,
        lateGraceMinutes: 5,
        overtimeBasis: "weekly",
        hoursBeforeOvertimePerPeriod: 40,
        dailyOvertimeHours: 8,
      });
      setSettingsLoaded(true);
    }
  }, []);

  /** Live company-time clock */
  useEffect(() => {
    setNowTz(DateTime.now().setZone(companyTimeZone));
    const id = setInterval(() => {
      setNowTz(DateTime.now().setZone(companyTimeZone));
    }, 1000);
    return () => clearInterval(id);
  }, [companyTimeZone]);

  /** Load schedule (in company TZ) */
  const fetchSchedule = useCallback(async () => {
    if (!user) return;
    try {
      const companyId = await resolveEmployeeCompany();
      const tz = await fetchCompanyTimeZone(companyId);
      await fetchTimeTrackingSettings(companyId);

      const qRef = query(
        collection(db, `companies/${companyId}/schedules`),
        where("employeeId", "==", user.uid),
      );
      const qSnap = await getDocs(qRef);
      const all = qSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => a.weekStartDate.localeCompare(b.weekStartDate));

      const now = DateTime.now().setZone(tz);
      const todayISO = now.toISODate();
      const ydayISO = now.minus({ days: 1 }).toISODate();

      const weekToday = scheduleCoveringDate(all, todayISO, tz);
      const weekYday = scheduleCoveringDate(all, ydayISO, tz);

      const shiftToday =
        weekToday?.shifts?.find((s) => s.date === todayISO) || null;
      const shiftYday =
        weekYday?.shifts?.find((s) => s.date === ydayISO) || null;

      function shiftWindow(shift, baseISO, tz) {
        if (!shift?.startTime || !shift?.stopTime) return null;

        const [sh, sm] = shift.startTime.split(":").map(Number);
        const [eh, em] = shift.stopTime.split(":").map(Number);

        const start = DateTime.fromISO(baseISO, { zone: tz }).set({
          hour: sh,
          minute: sm,
          second: 0,
          millisecond: 0,
        });

        let end = DateTime.fromISO(baseISO, { zone: tz }).set({
          hour: eh,
          minute: em,
          second: 0,
          millisecond: 0,
        });

        // Overnight shift: end is next day
        if (end.toMillis() <= start.toMillis()) end = end.plus({ days: 1 });

        return { start, end };
      }

      // If yesterday’s shift spills into “now”, treat that as the assigned shift.
      const ywin = shiftWindow(shiftYday, ydayISO, tz);
      const isInYdayOvernight = ywin && now >= ywin.start && now <= ywin.end;

      setAssignedShift(isInYdayOvernight ? shiftYday : shiftToday);
    } catch (err) {
      console.error("Error fetching schedule:", err);
      setAssignedShift(null);
    }
  }, [
    user,
    resolveEmployeeCompany,
    fetchCompanyTimeZone,
    fetchTimeTrackingSettings,
  ]);

  /** Live time entries for this user */
  useEffect(() => {
    if (!user) return;

    let unsubscribe;

    const init = async () => {
      try {
        const companyId = await resolveEmployeeCompany();
        const tz = await fetchCompanyTimeZone(companyId);
        await fetchTimeTrackingSettings(companyId);

        const workRef = collection(db, `companies/${companyId}/time_tracking`);
        const q = query(workRef, where("userId", "==", user.uid));

        unsubscribe = onSnapshot(q, (querySnapshot) => {
          const now = DateTime.now().setZone(tz);
          const todayISO = now.toISODate();
          const weekStart = now.startOf("week");

          let totalDay = 0;
          let totalWeek = 0;
          const todayEntries = [];
          let latestOpen = null;

          querySnapshot.forEach((docSnap) => {
            const data = docSnap.data();
            const entryDate = DateTime.fromISO(data.date, { zone: tz });

            // week totals
            if (entryDate >= weekStart) totalWeek += data.hoursWorked || 0;

            // today's list
            if (entryDate.toISODate() === todayISO) {
              totalDay += data.hoursWorked || 0;
              todayEntries.push({ id: docSnap.id, ...data });
            }

            // track *any* open session (latest)
            if (!data.clockOut) {
              if (
                !latestOpen ||
                new Date(data.clockIn) > new Date(latestOpen.clockIn)
              ) {
                latestOpen = { id: docSnap.id, ...data };
              }
            }
          });

          setDailyHours(totalDay);
          setWeeklyHours(totalWeek);
          setEntries(
            todayEntries.sort(
              (a, b) => new Date(a.clockIn) - new Date(b.clockIn),
            ),
          );
          setOpenEntryDoc(latestOpen); // may be yesterday or earlier
        });
      } catch (err) {
        console.error("Live tracking error:", err.message);
      }
    };

    init();
    return () => unsubscribe && unsubscribe();
  }, [
    user,
    resolveEmployeeCompany,
    fetchCompanyTimeZone,
    fetchTimeTrackingSettings,
  ]);

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  /** Self-tests (optional) */
  useEffect(() => {
    if (!DEBUG) return;
    (async () => {
      try {
        const companyId = await resolveEmployeeCompany();
        const tz = await fetchCompanyTimeZone(companyId);

        const base = DateTime.fromISO("2025-01-01T12:00:00", { zone: tz });
        const tests = [
          { name: "On time exact", now: base, grace: 5 },
          {
            name: "Late 3m within grace",
            now: base.plus({ minutes: 3 }),
            grace: 5,
          },
          {
            name: "Late 6m outside grace",
            now: base.plus({ minutes: 6 }),
            grace: 5,
          },
          {
            name: "Early 7m within window",
            now: base.minus({ minutes: 7 }),
            early: 10,
          },
          {
            name: "Early 12m blocked",
            now: base.minus({ minutes: 12 }),
            early: 10,
          },
        ];

        console.groupCollapsed("[TimeManagement] Self-tests");
        for (const t of tests) {
          const s = determineStatus(t.now, base, {
            enableEarlyClockIn: true,
            earlyClockInMinutes: t.early ?? 15,
            enableLateGrace: true,
            lateGraceMinutes: t.grace ?? 5,
          });
          console.log(`${t.name}:`, { result: s.status, details: s });
        }
        console.groupEnd();
      } catch (e) {
        console.warn("Self-tests skipped:", e?.message);
      }
    })();
  }, [resolveEmployeeCompany, fetchCompanyTimeZone]);

  function formatHoursMins(totalMinutes) {
    const mins = Math.max(0, Math.round(totalMinutes || 0));
    const h = Math.floor(mins / 60);
    const m = mins % 60;

    if (h <= 0) return `${m} min${m === 1 ? "" : "s"}`;
    if (m === 0) return `${h} hr${h === 1 ? "" : "s"}`;

    return `${h} hr${h === 1 ? "" : "s"} ${m} min${m === 1 ? "" : "s"}`;
  }

  function buildClockInStatusMessage(status, earlySecs = 0, lateSecs = 0) {
    if (status === "Late Clock-in") {
      const totalMins = Math.max(1, Math.ceil((lateSecs || 0) / 60)); // round up
      return `You clocked in late by ${formatHoursMins(totalMins)}`;
    }

    if (status === "Early Clock-in") {
      return `You clocked in early`;
    }

    return `You clocked in on time`;
  }

  /** Clock In */
  const handleClockIn = async () => {
    try {
      if (!user) throw new Error("User not authenticated.");
      if (!assignedShift)
        throw new Error("You are not scheduled to work today.");
      if (assignedShift.isOffDay) throw new Error("You have an off day today.");
      if (assignedShift.isHoliday)
        throw new Error("Today is a holiday. No clock-in allowed.");

      // Block when *any* open session exists
      if (openEntryDoc) {
        throw new Error("You’re already clocked in. Please clock out first.");
      }

      // Company + employee
      const companyId = await resolveEmployeeCompany();

      const employeeRef = doc(db, `companies/${companyId}/employees`, user.uid);
      const employeeSnap = await getDoc(employeeRef);
      if (!employeeSnap.exists()) throw new Error("Employee record not found.");
      const employee = employeeSnap.data();

      // Just-in-time TZ + settings
      const tzDoc = await getDoc(doc(db, "companies", companyId));
      const tz = tzDoc.data()?.timeZone || "UTC";
      setCompanyTimeZone(tz);
      setTzLoaded(true);

      const sref = doc(db, `companies/${companyId}/settings/timeTracking`);
      const ssnap = await getDoc(sref);
      const live = ssnap.exists() ? ssnap.data() : {};
      const effectiveSettings = {
        enableEarlyClockIn:
          typeof live.enableEarlyClockIn === "boolean"
            ? live.enableEarlyClockIn
            : ttSettings.enableEarlyClockIn ?? true,
        earlyClockInMinutes:
          typeof live.earlyClockInMinutes === "number"
            ? live.earlyClockInMinutes
            : ttSettings.earlyClockInMinutes ?? 15,
        enableLateGrace:
          typeof live.enableLateGrace === "boolean"
            ? live.enableLateGrace
            : ttSettings.enableLateGrace ?? true,
        lateGraceMinutes:
          typeof live.lateGraceMinutes === "number"
            ? live.lateGraceMinutes
            : ttSettings.lateGraceMinutes ?? 5,
      };

      // Times in company TZ
      const now = DateTime.now().setZone(tz);
      // const todayISO = now.toISODate();

      const isFirstClockInOfDay = !entries.length;

      // Shift start for today in same TZ
      const [shiftHour, shiftMinute] = assignedShift.startTime
        .split(":")
        .map(Number);

      const shiftStart = DateTime.fromISO(assignedShift.date, { zone: tz }).set(
        {
          hour: shiftHour,
          minute: shiftMinute,
          second: 0,
          millisecond: 0,
        },
      );
      const [endHour, endMinute] = assignedShift.stopTime
        .split(":")
        .map(Number);

      let shiftEnd = DateTime.fromISO(assignedShift.date, { zone: tz }).set({
        hour: endHour,
        minute: endMinute,
        second: 0,
        millisecond: 0,
      });

      if (shiftEnd.toMillis() <= shiftStart.toMillis()) {
        shiftEnd = shiftEnd.plus({ days: 1 }); // overnight
      }

      // ✅ ADD THIS BLOCK RIGHT HERE
      if (now.toMillis() > shiftEnd.toMillis()) {
        throw new Error(
          "This shift has already ended. Clock-in is no longer allowed.",
        );
      }

      const { status, earlySecs, lateSecs } = determineStatus(
        now,
        shiftStart,
        effectiveSettings,
      );

      if (DEBUG) {
        console.groupCollapsed("[ClockIn Debug]");
        console.log("TZ:", tz);
        console.log("now:", now.toISO());
        console.log("shiftStart:", shiftStart.toISO());
        console.log("settings:", effectiveSettings);
        console.log("diff (earlySecs, lateSecs):", { earlySecs, lateSecs });
        console.log("status:", status);
        console.groupEnd();
      }

      if (status === "BLOCK_EARLY") {
        const earliest = shiftStart.minus({
          minutes: effectiveSettings.earlyClockInMinutes ?? 15,
        });
        throw new Error(
          `Too early to clock in. Earliest allowed: ${earliest.toFormat(
            "t",
          )} (${tz}).`,
        );
      }
      // ✅ Source of truth = today's assigned shift (schedule)
      const workSiteId = assignedShift?.locationId || null;
      const workSiteName = assignedShift?.locationName || "";

      if (!workSiteId) {
        throw new Error(
          "Worksite missing on your shift. Contact your manager.",
        );
      }

      // Record: hybrid location (GPS + IP)
      const clockInLocation = await getLocation();

      const newEntryRef = doc(
        collection(db, `companies/${companyId}/time_tracking`),
      );
      await setDoc(newEntryRef, {
        id: newEntryRef.id,
        userId: user.uid,
        employeeName: employee.name,
        clockIn: now.toISO(),
        clockOut: null,
        hoursWorked: 0,
        date: assignedShift.date,
        status,
        clockInLocation: clockInLocation || { precision: "none" },
        // ✅ add these
        workSiteId,
        workSiteName,
      });
      // ✅ Employee inbox notification (simple first one)
      const clockInMessage = buildClockInStatusMessage(
        status,
        earlySecs,
        lateSecs,
      );

      await sendUserNotification(user.uid, {
        type: "clockInStatus",
        title: "Clock-in recorded",
        message: clockInMessage,
        companyId,
        date: assignedShift.date,
        time: now.toISO(),
        workSiteId,
        workSiteName,
        related: {
          timeEntryId: newEntryRef.id,
          clockInStatus: status,
          lateMins:
            status === "Late Clock-in" ? Math.ceil((lateSecs || 0) / 60) : 0,
          earlyMins:
            status === "Early Clock-in" ? Math.ceil((earlySecs || 0) / 60) : 0,
        },
      });

      // Notify if late (first clock-in only)
      if (status === "Late Clock-in" && isFirstClockInOfDay) {
        const minutesLate = Math.floor((lateSecs ?? 0) / 60);
        const notifId = `lateClockIn_${user.uid}_${assignedShift.date}`;
        await sendCompanyNotification(companyId, notifId, {
          type: "lateClockIn",
          employeeId: user.uid,
          employeeName: employee.name,
          shiftStart: assignedShift.startTime,
          clockInAt: now.toISO(),
          minutesLate,
          message: `${employee.name} clocked in ${minutesLate} min late.`,
          severity:
            minutesLate >= 30 ? "high" : minutesLate >= 10 ? "medium" : "low",
          related: { timeEntryId: newEntryRef.id, date: assignedShift.date },
        });
      }

      setError("");
    } catch (err) {
      setError(err.message);
      if (DEBUG) console.error("[ClockIn Error]", err);
    }
  };

  /** Clock Out (diff computed in company TZ) */
  /** Clock Out (diff computed in company TZ - freshly resolved) */
  const handleClockOut = async (entryId) => {
    try {
      if (!user) throw new Error("User not authenticated.");

      const companyId = await resolveEmployeeCompany();

      // ✅ Always fetch TZ fresh (avoid stale state edge cases)
      const companySnap = await getDoc(doc(db, "companies", companyId));
      const tz = companySnap.data()?.timeZone || "UTC";

      // (optional) keep state in sync for UI formatting
      setCompanyTimeZone(tz);
      setTzLoaded(true);

      const workRef = doc(db, `companies/${companyId}/time_tracking`, entryId);
      const docSnap = await getDoc(workRef);
      if (!docSnap.exists()) throw new Error("Clock-in data not found.");

      const data = docSnap.data();
      if (data.clockOut) throw new Error("Already clocked out.");

      // ✅ Use the freshly-resolved TZ for BOTH timestamps
      const now = DateTime.now().setZone(tz);
      const clockIn = DateTime.fromISO(data.clockIn, { zone: tz });

      const hours = Math.max(
        0,
        Number(now.diff(clockIn, "hours").hours.toFixed(2)),
      );

      await updateDoc(workRef, {
        clockOut: now.toISO(),
        hoursWorked: hours,
      });

      setError("");
    } catch (e) {
      setError(e?.message || "Failed to clock out.");
      if (DEBUG) console.error("[ClockOut Error]", e);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "N/A";
    return DateTime.fromISO(timestamp, { zone: companyTimeZone }).toFormat(
      "DDD t",
    );
  };

  // ---------- Weekly / Range Timeline (modal) ----------
  const loadWeekTimeline = useCallback(
    async (targetStartISO, maybeEndISO) => {
      if (!user) return;
      setWeekLoading(true);
      try {
        const companyId = await resolveEmployeeCompany();
        const tz = await fetchCompanyTimeZone(companyId);

        // Default: current week (7-day window)
        const startISO =
          targetStartISO || getWeekStartISO(DateTime.now().setZone(tz));
        const defaultEndISO = DateTime.fromISO(startISO, { zone: tz })
          .plus({ days: 6 })
          .toISODate();
        const endISO = maybeEndISO || defaultEndISO;

        const baseRef = collection(db, `companies/${companyId}/time_tracking`);
        const qRef = query(
          baseRef,
          where("userId", "==", user.uid),
          where("date", ">=", startISO),
          where("date", "<=", endISO),
          orderBy("date", "asc"),
        );
        const snap = await getDocs(qRef);

        const byDate = new Map();
        const completedEntries = [];

        snap.forEach((docSnap) => {
          const d = docSnap.data();
          const list = byDate.get(d.date) || [];
          list.push(d);
          byDate.set(d.date, list);

          const hrs = Number(d.hoursWorked);
          // Count *any* completed session with positive hours,
          // including paid leave entries.
          if (d.clockOut && Number.isFinite(hrs) && hrs > 0) {
            completedEntries.push(d);
          }
        });

        // Build list of all days from start → end (inclusive)
        const days = [];
        let cursor = DateTime.fromISO(startISO, { zone: tz }).startOf("day");
        const endDt = DateTime.fromISO(endISO, { zone: tz }).startOf("day");
        while (cursor <= endDt) {
          days.push(cursor.toISODate());
          cursor = cursor.plus({ days: 1 });
        }

        const rows = days.map((iso) => {
          const entries = (byDate.get(iso) || []).sort(
            (a, b) => new Date(a.clockIn || 0) - new Date(b.clockIn || 0),
          );
          const pretty = entries.map((e) => ({
            clockIn: e.clockIn
              ? DateTime.fromISO(e.clockIn, { zone: tz }).toFormat("t")
              : "—",
            clockOut: e.clockOut
              ? DateTime.fromISO(e.clockOut, { zone: tz }).toFormat("t")
              : "—",
            hours: (e.hoursWorked ?? 0).toFixed(2),
          }));
          const total = entries.reduce(
            (sum, e) => sum + (e.hoursWorked || 0),
            0,
          );
          return {
            iso,
            dayLabel: DateTime.fromISO(iso, { zone: tz }).toFormat(
              "ccc, dd LLL",
            ),
            entries: pretty,
            totalHours: total,
          };
        });

        // --- OVERTIME CALC FOR THIS RANGE ---
        let total = 0;
        let regular = 0;
        let overtime = 0;

        if (completedEntries.length) {
          if (ttSettings.overtimeBasis === "daily") {
            const byDayTotals = {};
            completedEntries.forEach((e) => {
              const hrs = Number(e.hoursWorked) || 0;
              total += hrs;
              const day = e.date;
              byDayTotals[day] = (byDayTotals[day] || 0) + hrs;
            });

            const limit = ttSettings.dailyOvertimeHours || 0;
            Object.values(byDayTotals).forEach((hrs) => {
              const h = Number(hrs) || 0;
              if (limit > 0) {
                const reg = Math.min(h, limit);
                const ot = Math.max(h - limit, 0);
                regular += reg;
                overtime += ot;
              } else {
                regular += h;
              }
            });
          } else {
            // "weekly"/period basis – treat this selected range as one "period"
            total = completedEntries.reduce(
              (sum, e) => sum + (Number(e.hoursWorked) || 0),
              0,
            );
            const threshold = ttSettings.hoursBeforeOvertimePerPeriod || 0;
            if (threshold > 0) {
              regular = Math.min(total, threshold);
              overtime = Math.max(total - threshold, 0);
            } else {
              regular = total;
            }
          }
        }

        setRangeOvertime({
          total: Number(total.toFixed(2)),
          regular: Number(regular.toFixed(2)),
          overtime: Number(overtime.toFixed(2)),
        });

        // Save both the “week start” (for prev/next) and the actual range
        setWeekStartISO(startISO);
        setRangeStartISO(startISO);
        setRangeEndISO(endISO);
        setWeekRows(rows);
        setWeekTotalHours(rows.reduce((s, r) => s + r.totalHours, 0));
      } catch (e) {
        console.error("Failed to load week timeline:", e?.message);
      } finally {
        setWeekLoading(false);
      }
    },
    [
      user,
      resolveEmployeeCompany,
      fetchCompanyTimeZone,
      ttSettings.overtimeBasis,
      ttSettings.hoursBeforeOvertimePerPeriod,
      ttSettings.dailyOvertimeHours,
    ],
  );

  const openWeekModal = async () => {
    const cid = await resolveEmployeeCompany();
    const tz = await fetchCompanyTimeZone(cid);
    const startISO = getWeekStartISO(DateTime.now().setZone(tz));
    await loadWeekTimeline(startISO);
    setWeekModalOpen(true);
  };

  const closeWeekModal = () => setWeekModalOpen(false);

  const shiftWeek = async (direction) => {
    if (!weekStartISO) return;
    const cid = await resolveEmployeeCompany();
    const tz = await fetchCompanyTimeZone(cid);
    const nextStart = DateTime.fromISO(weekStartISO, { zone: tz })
      .plus({ weeks: direction })
      .toISODate();
    await loadWeekTimeline(nextStart);
  };

  const pickWeekFromDate = async (anyISO) => {
    if (!anyISO) return;
    const cid = await resolveEmployeeCompany();
    const tz = await fetchCompanyTimeZone(cid);
    const start = DateTime.fromISO(anyISO, { zone: tz }).toISODate();
    await loadWeekTimeline(start);
  };

  const pickCustomRange = async (startISO, endISO) => {
    if (!startISO || !endISO) return;
    const cid = await resolveEmployeeCompany();
    const tz = await fetchCompanyTimeZone(cid);

    let start = DateTime.fromISO(startISO, { zone: tz });
    let end = DateTime.fromISO(endISO, { zone: tz });
    if (!start.isValid || !end.isValid) return;

    if (end < start) {
      const tmp = start;
      start = end;
      end = tmp;
    }

    await loadWeekTimeline(start.toISODate(), end.toISODate());
  };
  // ---------- /Weekly / Range Timeline (modal) ----------

  const openEntry = !!openEntryDoc;

  return (
    <div className="tm-wrapper">
      <nav className="employeetime-nav">
        <p className="employeetime-breadcrumb">
          <Link to="/employee-dashboard">Dashboard</Link> /{" "}
          <span>TimeManagement</span>
        </p>
      </nav>

      {/* Company Time (live clock) */}
      <div className="tm-clockbar">
        <div className="tm-clockline">
          <span className="tm-clock-value">
            {nowTz.toFormat("cccc, dd LLL yyyy • hh:mm:ss a")} (
            {companyTimeZone})
          </span>
        </div>
      </div>

      <div className="tm-card-container">
        <div className="tm-card">
          <h3>Assigned Shift</h3>
          <p className="tm-date">{nowTz.toFormat("DDD")}</p>
          {assignedShift ? (
            assignedShift.isOffDay ? (
              <p className="tm-off">🚫 Off Day</p>
            ) : assignedShift.isHoliday ? (
              <p className="tm-holiday">🎉 Holiday</p>
            ) : (
              <p className="tm-shift">
                🕒 {formatShiftTime(assignedShift.startTime, companyTimeZone)} –{" "}
                {formatShiftTime(assignedShift.stopTime, companyTimeZone)}
              </p>
            )
          ) : (
            <p>No schedule assigned.</p>
          )}
        </div>

        <div className="tm-card">
          <h3>Work Tracking</h3>
          <button
            className="tm-btn"
            onClick={handleClockIn}
            disabled={
              openEntry || !settingsLoaded || !tzLoaded || !assignedShift
            }
          >
            Clock In
          </button>

          {openEntryDoc && (
            <div className="tm-hint">
              You have an active session from{" "}
              <strong>{openEntryDoc.date}</strong>.{" "}
              <button
                className="tm-btn-outline"
                onClick={() => handleClockOut(openEntryDoc.id)}
                style={{ marginLeft: 8 }}
              >
                Clock Out
              </button>
            </div>
          )}
        </div>

        <div className="tm-card">
          <h3>Today's Work Log</h3>
          {entries.length === 0 ? (
            <p>No clock-ins today</p>
          ) : (
            <table className="tm-log-table">
              <thead>
                <tr>
                  <th>Clock In</th>
                  <th>Clock Out</th>
                  <th>Hours</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id}>
                    <td>🟢 {formatTime(entry.clockIn)}</td>
                    <td>
                      {entry.clockOut
                        ? `🔴 ${formatTime(entry.clockOut)}`
                        : "—"}
                    </td>
                    <td>{(entry.hoursWorked ?? 0).toFixed(2)} hrs</td>
                    <td>
                      {!entry.clockOut && (
                        <button
                          className="tm-btn-outline"
                          onClick={() => handleClockOut(entry.id)}
                        >
                          Clock Out
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {openEntryDoc && openEntryDoc.date !== nowTz.toISODate() && (
            <div className="tm-hint" style={{ marginTop: 8 }}>
              Active session started on <strong>{openEntryDoc.date}</strong>.
              You can clock out above.
            </div>
          )}
        </div>

        <div className="tm-card">
          <h3>Work Summary</h3>
          <p>
            <strong>Today's Hours:</strong> {dailyHours.toFixed(2)} hrs
          </p>
          <p>
            <strong>Weekly Hours:</strong> {weeklyHours.toFixed(2)} hrs
          </p>
          <div style={{ marginTop: 8 }}>
            <button className="tm-btn-outline" onClick={openWeekModal}>
              View Week / Range Timeline
            </button>
          </div>
        </div>

        {error && <p className="tm-error">{error}</p>}
      </div>

      {/* Weekly / Range Timeline Modal */}
      {weekModalOpen && (
        <div
          className="tm-modal-backdrop"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            backdropFilter: "blur(2px)",
          }}
          onClick={closeWeekModal}
        >
          <div
            className="tm-modal"
            style={{
              width: "min(900px, 95vw)",
              background: "white",
              borderRadius: 12,
              padding: 20,
              boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
              maxHeight: "90vh",
              overflow: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="tm-modal-header"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                marginBottom: 12,
              }}
            >
              <h3 style={{ margin: 0 }}>Work Timeline</h3>
              <button className="tm-btn-outline" onClick={closeWeekModal}>
                Close
              </button>
            </div>

            <div
              className="tm-modal-controls"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <button
                  className="tm-btn-outline"
                  onClick={() => shiftWeek(-1)}
                >
                  ◀ Prev Week
                </button>
                <button className="tm-btn-outline" onClick={() => shiftWeek(1)}>
                  Next Week ▶
                </button>

                <div
                  style={{
                    marginLeft: "auto",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <label style={{ fontSize: 14 }}>Jump to date:</label>
                  <input
                    type="date"
                    value={
                      weekStartISO
                        ? DateTime.fromISO(weekStartISO).toISODate()
                        : ""
                    }
                    onChange={(e) => pickWeekFromDate(e.target.value)}
                    style={{ padding: "6px 8px" }}
                  />
                </div>
              </div>

              {/* Custom range controls */}
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <label style={{ fontSize: 14 }}>Custom range:</label>
                <input
                  type="date"
                  value={rangeStartISO}
                  onChange={(e) => setRangeStartISO(e.target.value)}
                  style={{ padding: "6px 8px" }}
                />
                <span style={{ fontSize: 14 }}>to</span>
                <input
                  type="date"
                  value={rangeEndISO}
                  onChange={(e) => setRangeEndISO(e.target.value)}
                  style={{ padding: "6px 8px" }}
                />
                <button
                  className="tm-btn-outline"
                  onClick={() => pickCustomRange(rangeStartISO, rangeEndISO)}
                >
                  Apply
                </button>
              </div>
            </div>

            {/* Range label + overtime explanation */}
            <div style={{ marginBottom: 8, opacity: 0.85, fontSize: 13 }}>
              {rangeStartISO && rangeEndISO
                ? (() => {
                    const start = DateTime.fromISO(rangeStartISO);
                    const end = DateTime.fromISO(rangeEndISO);
                    const basisText =
                      ttSettings.overtimeBasis === "daily"
                        ? `daily > ${ttSettings.dailyOvertimeHours || 0}h`
                        : `period > ${ttSettings.hoursBeforeOvertimePerPeriod ||
                            0}h`;
                    return `For ${start.toFormat(
                      "dd LLL yyyy",
                    )} – ${end.toFormat("dd LLL yyyy")}  (${basisText})`;
                  })()
                : ""}
            </div>

            {/* Overtime chips */}
            {rangeOvertime.total > 0 && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  marginBottom: 12,
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    fontSize: 13,
                    opacity: 0.9,
                    marginRight: 4,
                    fontWeight: 500,
                  }}
                >
                  Hours overview:
                </span>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      padding: "4px 10px",
                      borderRadius: 999,
                      background: "#f3f4f6",
                      fontSize: 13,
                    }}
                  >
                    Total <strong>{rangeOvertime.total.toFixed(2)} hrs</strong>
                  </div>
                  <div
                    style={{
                      padding: "4px 10px",
                      borderRadius: 999,
                      background: "#ecfdf5",
                      fontSize: 13,
                      color: "#15803d",
                    }}
                  >
                    Regular{" "}
                    <strong>{rangeOvertime.regular.toFixed(2)} hrs</strong>
                  </div>
                  <div
                    style={{
                      padding: "4px 10px",
                      borderRadius: 999,
                      background: "#fef2f2",
                      fontSize: 13,
                      color: "#b91c1c",
                    }}
                  >
                    Overtime{" "}
                    <strong>{rangeOvertime.overtime.toFixed(2)} hrs</strong>
                  </div>
                </div>
              </div>
            )}

            {weekLoading ? (
              <p>Loading…</p>
            ) : (
              <table className="tm-log-table" style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>Day</th>
                    <th style={{ textAlign: "left" }}>Clock In</th>
                    <th style={{ textAlign: "left" }}>Clock Out</th>
                    <th style={{ textAlign: "left" }}>Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {weekRows.map((row) => {
                    if (row.entries.length === 0) {
                      return (
                        <tr key={row.iso}>
                          <td>{row.dayLabel}</td>
                          <td colSpan={2}>—</td>
                          <td>0.00</td>
                        </tr>
                      );
                    }
                    const [first, ...rest] = row.entries;
                    return (
                      <>
                        <tr key={row.iso}>
                          <td>{row.dayLabel}</td>
                          <td>🟢 {first.clockIn}</td>
                          <td>
                            {first.clockOut !== "—"
                              ? `🔴 ${first.clockOut}`
                              : "—"}
                          </td>
                          <td>{first.hours}</td>
                        </tr>
                        {rest.map((e, idx) => (
                          <tr key={`${row.iso}-${idx}`}>
                            <td></td>
                            <td>🟢 {e.clockIn}</td>
                            <td>
                              {e.clockOut !== "—" ? `🔴 ${e.clockOut}` : "—"}
                            </td>
                            <td>{e.hours}</td>
                          </tr>
                        ))}
                        <tr>
                          <td style={{ fontWeight: 600 }}>Daily Total</td>
                          <td></td>
                          <td></td>
                          <td style={{ fontWeight: 600 }}>
                            {row.totalHours.toFixed(2)}
                          </td>
                        </tr>
                      </>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td style={{ fontWeight: 700 }}>Range Total</td>
                    <td></td>
                    <td></td>
                    <td style={{ fontWeight: 700 }}>
                      {weekTotalHours.toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
